import { readTransientUpstream } from '@kit/shared/upstream-error';

/**
 * Frames Sentry can group on when none of them are our code.
 *
 * A server action that fails inside the bundled Next.js runtime produces a
 * stack made only of those frames. Sentry then groups by that stack, so a
 * gateway failure and an unrelated invite error become one issue. Those
 * events get a fingerprint from the message instead. A stack that includes
 * an in-app application frame keeps Sentry's normal grouping.
 */
export interface GroupingFrame {
  filename?: string;
  abs_path?: string;
  module?: string;
  function?: string;
  in_app?: boolean;
}

export interface GroupingException {
  type?: string;
  value?: string;
  stacktrace?: { frames?: GroupingFrame[] | null } | null;
}

export interface GroupingEvent {
  message?: string;
  level?: string;
  fingerprint?: string[];
  tags?: Record<string, string | number | boolean | null | undefined>;
  exception?: { values?: Array<GroupingException | null> | null } | null;
}

export interface GroupingHint {
  originalException?: unknown;
}

const FRAMEWORK_PATH =
  /(?:^|[/\\])node_modules[/\\](?:\.pnpm[/\\][^/\\]+[/\\]node_modules[/\\])?(?:next|react-dom|react)[/\\]|[/\\]next[/\\]dist[/\\]|node:(?:internal|async_hooks)|app-page(?:-turbo)?\.runtime|webpack-internal:|<anonymous>/i;

const UUID =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;

const REQUEST_ID =
  /\b(?:x-request-id|request[_ -]?id|cf-ray|ray id|sb-request-id|trace[_ -]?id)\b\s*[:=]?\s*[A-Za-z0-9_.:-]{4,}/gi;

const LONG_HEX = /\b[0-9a-f]{16,}\b/gi;

const NUMBER = /\b\d+\b/g;

export function applySentryGrouping<T>(event: T, hint?: GroupingHint): T {
  const target = event as GroupingEvent;
  const transient = readTransientForEvent(target, hint);

  if (transient) {
    target.level = 'warning';
    target.tags = { ...target.tags, transient: 'true' };
    target.fingerprint = ['transient-upstream', transient.status];

    return event;
  }

  const frames = collectFrames(target);

  if (frames.length === 0 || frames.some(isInAppApplicationFrame)) {
    return event;
  }

  target.fingerprint = [
    'framework-message',
    normalizeGroupingMessage(exceptionMessage(target, hint)),
  ];

  return event;
}

export function isFrameworkFrame(frame: GroupingFrame): boolean {
  const path = [frame.filename, frame.abs_path, frame.module]
    .filter(
      (part): part is string => typeof part === 'string' && part.length > 0,
    )
    .join(' ');

  if (FRAMEWORK_PATH.test(path)) {
    return true;
  }

  const name = frame.function ?? '';

  return (
    name === 'process.processTicksAndRejections' ||
    name === 'AsyncLocalStorage.run'
  );
}

export function isInAppApplicationFrame(frame: GroupingFrame): boolean {
  return frame.in_app === true && !isFrameworkFrame(frame);
}

/**
 * A stable grouping key.
 *
 * JSON blobs contribute their status, code, and message fields, so a request
 * id sitting beside them cannot merge two different failures. Ids, request
 * ids, and numbers are then removed from whatever text remains.
 */
export function normalizeGroupingMessage(message: string): string {
  const extracted = extractGroupingText(message.trim()) ?? message.trim();
  const stripped = extracted
    .replace(UUID, '<id>')
    .replace(REQUEST_ID, '<id>')
    .replace(LONG_HEX, '<id>')
    .replace(NUMBER, '<n>')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 200)
    .trim();

  return stripped || 'empty';
}

function readTransientForEvent(event: GroupingEvent, hint?: GroupingHint) {
  const fromHint = readTransientUpstream(hint?.originalException);

  if (fromHint) {
    return fromHint;
  }

  let sawTransientType = false;

  for (const value of event.exception?.values ?? []) {
    if (!value) continue;

    if (value.type === 'TransientUpstreamError') {
      sawTransientType = true;
    }

    const fromValue = readTransientUpstream(value.value);

    if (fromValue) {
      return fromValue;
    }
  }

  if (sawTransientType) {
    return { status: 'unknown' as const };
  }

  return readTransientUpstream(event.message);
}

function collectFrames(event: GroupingEvent): GroupingFrame[] {
  const frames: GroupingFrame[] = [];

  for (const value of event.exception?.values ?? []) {
    for (const frame of value?.stacktrace?.frames ?? []) {
      if (frame) frames.push(frame);
    }
  }

  return frames;
}

function exceptionMessage(event: GroupingEvent, hint?: GroupingHint): string {
  const parts: string[] = [];

  for (const value of event.exception?.values ?? []) {
    if (!value) continue;

    const text = [value.type, value.value]
      .filter(
        (part): part is string => typeof part === 'string' && part.length > 0,
      )
      .join(': ');

    if (text) parts.push(text);
  }

  if (parts.length > 0) {
    return parts.join(' | ');
  }

  if (typeof event.message === 'string' && event.message.trim()) {
    return event.message;
  }

  const original = hint?.originalException;

  if (original instanceof Error && original.message) {
    return original.message;
  }

  if (typeof original === 'string') {
    return original;
  }

  return '';
}

function extractGroupingText(message: string): string | null {
  const parsed = parseContainer(message.replace(/^Error:\s*/, ''));

  if (parsed === null) {
    return null;
  }

  return groupingText(parsed, 0);
}

function groupingText(value: unknown, depth: number): string | null {
  if (depth > 4 || value == null) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const parsed = parseContainer(trimmed);

    if (parsed !== null) {
      const inner = groupingText(parsed, depth + 1);

      if (inner) return inner;
    }

    return trimmed || null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (Array.isArray(value)) {
    const parts = value
      .map((item) => groupingText(item, depth + 1))
      .filter((item): item is string => Boolean(item));

    return parts.length > 0 ? parts.join(' ') : null;
  }

  if (typeof value !== 'object') {
    return null;
  }

  const record = value as Record<string, unknown>;
  const status = scalarField(record.status ?? record.statusCode);
  const code = scalarField(record.code);
  const message = groupingText(
    record.message ?? record.error ?? record.error_description,
    depth + 1,
  );
  const parts: string[] = [];

  if (status) parts.push(`status=${status}`);
  if (code) parts.push(`code=${code}`);
  if (message) parts.push(`message=${message}`);

  return parts.length > 0 ? parts.join(' ') : null;
}

function scalarField(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();

  return trimmed || null;
}

function parseContainer(text: string): unknown | null {
  const trimmed = text.trim();

  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return null;
  }

  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return null;
  }
}
